
import ResourceContract from '../contracts/Resource';
import ValidatableContract from '../contracts/Validatable';
import Validator from '../support/Validator';

/**
 * Resource abstract
 * @abstract
 **/
abstract class Resource implements ValidatableContract
{
    /**
     * Resource attributes
     * @type {Object}
     **/
    protected attributes: any = {};

    /**
     * True when resource is persisted in storage
     * @type {Boolean}
     **/
    exists: boolean = false;

    /**
     * Validation errors
     * @type {Object}
     **/
    errors: any = {};

    /**
     * True when resource is being saved
     * @type {Boolean}
     **/
    saving: boolean = false;


    /**
     * Resource constructor
     * @param {Object} [attributes={}]
     * @param {Boolean} [exists=false]
     **/
    constructor( attributes: any = {}, exists: boolean = false )
    {
        this.exists = exists;
        this.relations( attributes );

        const defaults = this.class.attributes;
        attributes = { ...defaults, ...attributes };

        if( exists ) this.attributes = attributes;

        Object.assign( this, attributes );
    }


    /**
     * Instantiate resource relations
     * @param {Object} attributes
     */
    private relations( attributes: any ): any
    {
        // dont store relationships under resources attributes
        const relations = this.class.relations || {};
        for( const relationship in relations ) {
            const related = relations[relationship].related as ResourceContract;

            if( relationship in attributes ) {
                let resources = attributes[relationship];
                if( Array.isArray( resources ) )
                    resources = resources.map( attributes => new related( attributes, true ) );
                else resources = new related( resources, true );

                this[relationship] = resources;
                delete attributes[relationship];

            } else if( ! this.exists ) {
                this[relationship] = relations[relationship].default;
            }
        }

        return attributes;
    }


    /**
     * Register resource
     * @param {ResourceContract} resource
     * @return {void}
     **/
    static register( resource: ResourceContract ): void
    {
        //
    }


    /**
     * Returns true if resource has changed, false otherwise
     * @return {Boolean}
     **/
    get changed(): boolean
    {
        for( const attribute in this.class.attributes ) {
            if( this[attribute] !== this.attributes[attribute] )
                return true;
        }

        return false;
    }


    /**
     * Get changed attributes
     * @return {Object}
     **/
    get changes(): any
    {
        const changes = {};
        for( const attribute in this.class.attributes ) {
            if( this[attribute] !== this.attributes[attribute] )
                changes[attribute] = this[attribute];
        }

        return changes;
    }


    /**
     * Get current attributes
     * @return {Object}
     */
    get current(): any
    {
        const attributes = {};
        for( const attribute in this.class.attributes )
            attributes[attribute] = this[attribute];

        return attributes;
    }


    /**
     * Get instance constructor
     * @return {ResourceContract}
     **/
    get class(): ResourceContract
    {
        return this.constructor as ResourceContract;
    }


    /**
     * Get resource id attribute
     * @return {Object}
     **/
    get id(): number
    {
        return this.attributes[this.class.handle];
    }


    /**
     * Revert any applied changes
     * @return {void}
     **/
    revert(): void
    {
        Object.assign( this, this.attributes );
    }


    /**
     * Get resource validation rules
     * @return {Object}
     **/
    rules(): any
    {
        return {};
    }


    /**
     * Sync the original attributes with the current.
     * @param {Object} [attributes={}]
     * @return void
     */
    sync( attributes = {} ): void
    {
        attributes = { ...this.attributes, ...this.changes, ...attributes };

        // dont store relationships under resources attributes
        this.relations( attributes );

        this.attributes = attributes;
        this.exists = true;

        Object.assign( this, attributes );
    }


    /**
     * Validate resource
     * @return {Boolean}
     **/
    validate(): boolean
    {
        return Validator.validate( this );
    }
}

export default Resource;
